import React, { useState, useEffect, useRef } from 'react';
import { X, Package, Upload, AlertCircle, CheckCircle, Loader2, FileImage, Video, Trash2 } from 'lucide-react';
import { useReturns } from '../context/ReturnContext';

const ReturnModal = ({
  isOpen,
  onClose,
  order,
  onReturnSubmitted = null
}) => {
  const modalRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const {
    returnReasons,
    loading,
    error,
    reasonsLoading,
    submitReturnRequest,
    clearError
  } = useReturns();

  const [formData, setFormData] = useState({
    returnType: 'return',
    reasonId: '',
    description: '',
    image: null,
    video: null
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isSubmitting]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        returnType: 'return',
        reasonId: '',
        description: '',
        image: null,
        video: null
      });
      setImagePreview(null);
      setVideoPreview(null);
      setIsSubmitting(false);
      setSubmitSuccess(false);
      setValidationErrors({});
      clearError();
    }
  }, [isOpen, clearError]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setValidationErrors(prev => ({
          ...prev,
          image: 'Please select a valid image file'
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors(prev => ({
          ...prev,
          image: 'Image size must be less than 5MB'
        }));
        return;
      }

      setFormData(prev => ({ ...prev, image: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);

      // Clear validation error
      setValidationErrors(prev => ({ ...prev, image: null }));
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        setValidationErrors(prev => ({
          ...prev,
          video: 'Please select a valid video file'
        }));
        return;
      }

      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setValidationErrors(prev => ({
          ...prev,
          video: 'Video size must be less than 50MB'
        }));
        return;
      }

      setFormData(prev => ({ ...prev, video: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setVideoPreview(e.target.result);
      reader.readAsDataURL(file);

      // Clear validation error
      setValidationErrors(prev => ({ ...prev, video: null }));
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: null }));
    setVideoPreview(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.reasonId) {
      errors.reasonId = 'Please select a reason for return';
    }

    if (!formData.description.trim()) {
      errors.description = 'Please provide a description';
    } else if (formData.description.trim().length < 10) {
      errors.description = 'Description must be at least 10 characters';
    }

    if (!formData.image && !formData.video) {
      errors.media = 'Please upload at least one image or video';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const returnData = {
        orderId: order.id,
        returnType: formData.returnType,
        reasonId: parseInt(formData.reasonId),
        description: formData.description.trim(),
        image: formData.image,
        video: formData.video
      };

      console.log('Submitting return request:', returnData);

      const result = await submitReturnRequest(returnData);

      if (result.success) {
        console.log('Return request submitted successfully:', result);
        setSubmitSuccess(true);

        // Show success message briefly before closing
        setTimeout(() => {
          // Call the success callback if provided
          if (onReturnSubmitted) {
            onReturnSubmitted(order.id);
          }

          // Close the modal
          onClose();
        }, 2000);
      } else {
        console.error('Return request submission failed:', result.error);
        // Error is already set in the context, so it will be displayed
      }
    } catch (error) {
      console.error('Error submitting return request:', error);
      // Error handling is done in the context
    } finally {
      // Keep isSubmitting true until modal closes to prevent multiple submissions
      // Don't set it to false here as we want to keep the button disabled
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
      clearError();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen || !order) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="return-modal-title"
        tabIndex={-1}
      >
        <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white shadow-2xl rounded-2xl transform transition-all duration-300 scale-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
            <div>
              <h2 id="return-modal-title" className="text-2xl font-bold text-gray-900">
                Return Request
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Order {order.orderNumber} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
              </p>
              {submitSuccess && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                  <CheckCircle className="w-4 h-4" />
                  Request submitted successfully!
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="p-6">
              {/* Order Items Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-orange-600" />
                  Order Items
                </h3>
                <div className="flex flex-wrap gap-3">
                  {order.items?.slice(0, 4).map((item, index) => (
                    <div key={item.id} className="relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        loading="lazy"
                      />
                      {item.quantity > 1 && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {item.quantity}
                        </div>
                      )}
                    </div>
                  ))}
                  {order.items?.length > 4 && (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        +{order.items.length - 4}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Return Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Return Reason */}
                <div>
                  <label htmlFor="return-reason" className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Return
                  </label>
                  {reasonsLoading ? (
                    <div className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      <span className="text-gray-600">Loading reasons...</span>
                    </div>
                  ) : (
                    <select
                      id="return-reason"
                      value={formData.reasonId}
                      onChange={(e) => handleInputChange('reasonId', e.target.value)}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 ${
                        validationErrors.reasonId ? 'border-red-300' : 'border-gray-300'
                      } ${isSubmitting ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
                      required
                    >
                      <option value="">Select a reason</option>
                      {returnReasons.map((reason) => (
                        <option key={reason.id} value={reason.id}>
                          {reason.title}
                        </option>
                      ))}
                    </select>
                  )}
                  {validationErrors.reasonId && (
                    <p className="text-sm text-red-600 mt-1">{validationErrors.reasonId}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="return-description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="return-description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Please describe the issue in detail..."
                    rows={4}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 resize-none ${
                      validationErrors.description ? 'border-red-300' : 'border-gray-300'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
                    required
                    minLength={10}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {validationErrors.description ? (
                      <p className="text-sm text-red-600">{validationErrors.description}</p>
                    ) : (
                      <p className="text-sm text-gray-500">
                        Minimum 10 characters ({formData.description.length}/10)
                      </p>
                    )}
                  </div>
                </div>

                {/* File Uploads */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Upload Evidence (Required)
                  </label>

                  {/* Image Upload */}
                  <div className="mb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <label
                        htmlFor="image-upload"
                        className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <FileImage className="w-4 h-4" />
                        <span>Upload Image</span>
                      </label>
                      <input
                        ref={imageInputRef}
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={isSubmitting}
                        className="hidden"
                      />
                      <span className="text-sm text-gray-500">Max 5MB</span>
                    </div>

                    {imagePreview && (
                      <div className="relative inline-block">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          disabled={isSubmitting}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {validationErrors.image && (
                      <p className="text-sm text-red-600 mt-1">{validationErrors.image}</p>
                    )}
                  </div>

                  {/* Video Upload */}
                  <div className="mb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <label
                        htmlFor="video-upload"
                        className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Video className="w-4 h-4" />
                        <span>Upload Video</span>
                      </label>
                      <input
                        ref={videoInputRef}
                        id="video-upload"
                        type="file"
                        accept="video/*"
                        onChange={handleVideoChange}
                        disabled={isSubmitting}
                        className="hidden"
                      />
                      <span className="text-sm text-gray-500">Max 50MB</span>
                    </div>

                    {videoPreview && (
                      <div className="relative inline-block">
                        <video
                          src={videoPreview}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                          controls
                        />
                        <button
                          type="button"
                          onClick={removeVideo}
                          disabled={isSubmitting}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {validationErrors.video && (
                      <p className="text-sm text-red-600 mt-1">{validationErrors.video}</p>
                    )}
                  </div>

                  {validationErrors.media && (
                    <p className="text-sm text-red-600">{validationErrors.media}</p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 text-sm font-medium mb-1">
                      <AlertCircle className="w-4 h-4" />
                      Error
                    </div>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {submitSuccess && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 text-sm font-medium mb-1">
                      <CheckCircle className="w-4 h-4" />
                      Request Submitted Successfully!
                    </div>
                    <p className="text-sm text-green-700">
                      Your return request has been submitted. We'll review it and get back to you soon.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || submitSuccess}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : submitSuccess ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Submitted!
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReturnModal;
